import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { useSwipeDownToClose } from '../hooks/useSwipeDownToClose';
import { EffortRuler } from '../shared-ui/EffortRuler';
import type { EffortLevel, RegistryModel } from '../hooks/useModels';
import type { ContextFullness } from '@voice-assistant/shared';
import { useContextFullnessStore } from '../state/contextFullnessStore';

// Provider indicator — REAL attribution from the backend-resolved provider
// (SE-14, Phase 0b change 6): a Kimi/Qwen/local session must never fall through
// to a Claude badge. Unknown providers get a neutral badge, not a wrong one.
const PROVIDER_BADGES: Record<string, { icon: string; short: string; name: string }> = {
  anthropic: { icon: '\u{1f7e2}', short: 'C', name: 'Claude' },
  'claude-cli': { icon: '\u{1f7e2}', short: 'C', name: 'Claude CLI' },
  google: { icon: '\u{1f535}', short: 'G', name: 'Gemini' },
  kimi: { icon: '\u{1f319}', short: 'K', name: 'Kimi' },
  qwen: { icon: '\u{1f7e3}', short: 'Q', name: 'Qwen' },
  openai: { icon: '\u{1f7e1}', short: 'O', name: 'OpenAI' },
  agy: { icon: '\u{1f7e1}', short: 'A', name: 'Antigravity' },
  'local-5090': { icon: '\u{1f3e0}', short: 'L', name: 'Local' },
};
const NEUTRAL_BADGE = { icon: '\u{26aa}', short: '?', name: 'Unknown' };

interface SessionHealth {
  fileSize?: number;
  lastModified?: number;
  createdAt?: number;
  contextPct?: number;
  inputTokens?: number;
  contextWindow?: number;
  modelsUsed?: { model: string; count: number }[];
  toolCallCount?: number;
  docsUpdateStats?: { count: number; lastAt?: number };
  autoDocsUpdateThreshold?: number;
  autoDocsPaused?: boolean;
  lastHealthAt?: number;
}

interface Props {
  checkedKeys: string[];
  sessionMeta?: { numTurns?: number; totalCostUsd?: number };
  usage: any;
  health: SessionHealth | null;
  /** True while health data is being fetched (null health + active session). Shows pulsing indicator. */
  loading?: boolean;
  providerInfo?: { provider: string; model?: string; autoMode?: boolean; reason?: string } | null;
  currentSessionId?: string | null;
  onToggleAutoDocsPaused?: (paused: boolean) => void;
  /** Session tuning props — moved here from the dissolved InlineTuningControl pill */
  sessionModel?: string | null;
  effort?: EffortLevel | null;
  models?: RegistryModel[];
  enable1mContext?: boolean;
  onEffortChange?: (next: EffortLevel) => void;
  on1mContextChange?: (enable: boolean) => void;
  /**
   * Render variant. 'inline' (default) is the standalone pill used in-chat.
   * 'navslot' renders a compact icon-over-label body (percentage over the
   * context-window) sized to sit inside a bottom-nav slot, and floats its
   * expanded panel up as a fixed bottom-sheet above the nav (Bottom-nav r6).
   */
  variant?: 'inline' | 'navslot';
  /**
   * When true, suppress the context-% text in the compact pill.
   * Used when the active session dot already shows the % — avoids duplication.
   * The expanded sheet still shows the full token breakdown.
   */
  hidePct?: boolean;
  /** Called when the user adjusts the auto-save threshold via the stepper. */
  onThresholdChange?: (threshold: number) => void;
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

function formatAge(ms: number): string {
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function pctColor(pct: number): string {
  if (pct < 40) return '#44cc88';
  if (pct < 70) return '#ccaa33';
  if (pct < 85) return '#cc8833';
  return '#e57373';
}

/** Short display label for a model ID — used in compact top-bar pill */
function shortModel(m?: string | null): string {
  if (!m) return '';
  const l = m.toLowerCase();
  if (l.includes('opus')) return 'Opus';
  if (l.includes('sonnet')) return 'Sonnet';
  if (l.includes('haiku')) return 'Haiku';
  if (l.includes('flash-lite') || l === 'fl') return 'FL';
  if (l.includes('flash')) return 'Flash';
  if (l.includes('2.5-pro') || l.includes('gemini-pro')) return 'Pro';
  if (l.includes('gemini-3') || l.includes('g3')) return 'G3';
  if (l === 'unknown') return '?';
  if (l === 'imagen') return 'Img';
  if (l === 'veo') return 'Veo';
  return m.split('-')[0] || m;
}

/** Full versioned label for a model ID — used in the expanded context pill */
function fullModelLabel(m: string): string {
  if (!m) return '?';
  const l = m.toLowerCase();
  // Anthropic: claude-{family}-{major}-{minor}[-{date}]
  // e.g. claude-opus-4-6, claude-opus-4-7-20250514, claude-haiku-4-5-20251001
  const ant = l.match(/claude-(opus|sonnet|haiku)-(\d+)-(\d+)/);
  if (ant) {
    const family = ant[1][0].toUpperCase() + ant[1].slice(1);
    return `${family} ${ant[2]}.${ant[3]}`;
  }
  // Google: gemini-{version}-{family}[-{variant}]
  // e.g. gemini-2.5-flash, gemini-2.5-flash-lite, gemini-2.5-pro
  const gem = l.match(/gemini-(\d+\.?\d*)-(flash|pro|ultra)/);
  if (gem) {
    const isLite = l.includes('flash-lite');
    const family = gem[2][0].toUpperCase() + gem[2].slice(1);
    return `${family}${isLite ? ' Lite' : ''} ${gem[1]}`;
  }
  if (l === 'imagen') return 'Imagen';
  if (l === 'veo') return 'Veo';
  // Fallback: capitalize shortModel result
  return shortModel(m) || m;
}

// Backend accepts 50-95 in 5-step increments.
const THRESHOLD_MIN = 50;
const THRESHOLD_MAX = 95;
const THRESHOLD_STEP = 5;
const THRESHOLD_DEFAULT = 71;

export function ContextBar({ checkedKeys, sessionMeta = {}, health, loading, providerInfo, currentSessionId, onToggleAutoDocsPaused, sessionModel, effort, models, enable1mContext, onEffortChange, on1mContextChange, variant = 'inline', hidePct = false, onThresholdChange }: Props) {
  const isNavSlot = variant === 'navslot';
  const [expanded, setExpanded] = useState(false);
  // Local threshold state for immediate stepper feedback; syncs from health when it arrives.
  const [localThreshold, setLocalThreshold] = useState<number | null>(null);
  const effectiveThreshold = localThreshold ?? health?.autoDocsUpdateThreshold ?? THRESHOLD_DEFAULT;
  // Keep local in sync when health data arrives (but don't overwrite a pending local change)
  useEffect(() => {
    if (health?.autoDocsUpdateThreshold != null) setLocalThreshold(health.autoDocsUpdateThreshold);
  }, [health?.autoDocsUpdateThreshold]);

  const adjustThreshold = useCallback((delta: number) => {
    const next = Math.min(THRESHOLD_MAX, Math.max(THRESHOLD_MIN, effectiveThreshold + delta));
    setLocalThreshold(next);
    onThresholdChange?.(next);
  }, [effectiveThreshold, onThresholdChange]);
  // r19 (unified-drawer P2): swipe-down-to-close on the expanded sheet's grab handle.
  const sheetSwipe = useSwipeDownToClose(() => setExpanded(false));
  const rootRef = useRef<HTMLDivElement>(null);

  // Capability checks for session tuning (effort ruler + 1M toggle)
  const activeModel = models?.find((m) =>
    sessionModel ? (m.modelId === sessionModel || sessionModel.includes(m.modelId) || m.modelId.includes(sessionModel)) : m.isDefault,
  );
  const caps = activeModel?.capabilities;
  const supportsEffort = !!(caps?.supportsEffort && caps?.supportedEffortLevels?.length);
  // Reachability guard: if 1M is CURRENTLY enabled, the toggle MUST render so it can be turned
  // back off — even when the capability lookup misses (registry not yet loaded) or the resolved
  // model's caps lack supports1MContext (e.g. a `cascade` session that carries enable1mContext=true).
  // Without the `|| enable1mContext` fallback the toggle disappears and 1M becomes un-turn-off-able.
  const supports1M = !!(caps?.supports1MContext) || !!enable1mContext;

  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (e: Event) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      setExpanded(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [expanded]);

  // Listen for the context-dot tap event so tapping the active session dot
  // (which now shows the %) opens this expanded sheet.
  useEffect(() => {
    const handler = () => setExpanded(true);
    window.addEventListener('va-open-contextbar', handler);
    return () => window.removeEventListener('va-open-contextbar', handler);
  }, []);

  // Occupancy union (context-fullness Phase 0b, #1976) — server-computed and
  // held by contextFullnessStore; this component RENDERS it and never derives
  // a pct from raw tokens/window (SE-09 no-fabrication).
  const storedFullness = useContextFullnessStore((s) => (currentSessionId ? s.bySession[currentSessionId] : undefined));
  const fullness: ContextFullness = storedFullness ?? { kind: 'pending' };
  const isProjected = fullness.kind === 'projected';
  const reading = fullness.kind === 'measured' || fullness.kind === 'projected' ? fullness : null;
  // Any settled-or-projected union kind renders an occupancy label (unmetered
  // renders '—'); only 'pending' falls through to the msgs/empty fallback.
  const showsOccupancy = fullness.kind !== 'pending';

  // measured `NN%` · projected `~NN%` · unknown-window `NNk / ?` · unmetered `—`.
  const fullnessLabel = (() => {
    switch (fullness.kind) {
      case 'measured': return `${fullness.pct}%`;
      case 'projected': return `~${fullness.pct}%`;
      case 'unknown-window': return `${formatTokens(fullness.contextTokens)} / ?`;
      default: return '—';
    }
  })();
  const fullnessTitle = (() => {
    switch (fullness.kind) {
      case 'measured': return `${formatTokens(fullness.contextTokens)} / ${formatTokens(fullness.contextWindow)} tokens`;
      case 'projected': return `~${formatTokens(fullness.contextTokens)} / ${formatTokens(fullness.contextWindow)} tokens — projected mid-turn`;
      case 'unknown-window': return `${formatTokens(fullness.contextTokens)} tokens — context window unknown`;
      case 'unmetered':
        return fullness.reason === 'stale-anchor'
          ? 'Context not metered — the last reading went stale'
          : 'Context not metered — no usage reported by the provider';
      default: return 'Context info';
    }
  })();

  const hasMeta = sessionMeta.numTurns != null || sessionMeta.totalCostUsd != null;
  const hasProvider = !!providerInfo;
  const noSession = !currentSessionId;

  const color = !reading ? 'var(--text-secondary)' : pctColor(reading.pct);
  // Projected is display-only and muted — it never raises the ⚠ warn state.
  const warn = fullness.kind === 'measured' && fullness.pct > 70;

  // Provider badge (real attribution — no binary Gemini/Claude fallback).
  const providerBadge = (providerInfo && PROVIDER_BADGES[providerInfo.provider]) || { ...NEUTRAL_BADGE, name: providerInfo?.provider ?? NEUTRAL_BADGE.name };
  const isGoogle = providerInfo?.provider === 'google';

  // Model breakdown
  const modelsUsed = health?.modelsUsed ?? [];
  const distinctModels = modelsUsed.filter((m) => m.model !== 'unknown').length;

  // Tool & docs stats
  const toolCallCount = health?.toolCallCount ?? 0;
  const docsUpdateStats = health?.docsUpdateStats;
  const autoDocsPaused = health?.autoDocsPaused ?? false;

  const modelLabel = shortModel(providerInfo?.model);

  // Divider helper for expanded sections
  const sectionStyle = { borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px', marginTop: '4px' };

  // Pill is always tappable when there's a session (even without context data)
  const tappable = !noSession;

  // Compact pill content: progressively show what's available.
  // Empty-state (`new session, no tokens, no turns yet`) intentionally
  // returns null — see the early-return below that hides the whole pill
  // when there's nothing meaningful to show beyond the placeholder.
  const renderPillContent = () => {
    if (loading) return <span class="ctx-loading-dot" />;
    if (noSession) return '—';
    if (showsOccupancy && !hidePct) {
      // Projected renders muted — a display-only mid-turn estimate, never ⚠.
      return <span key="pct" class={isProjected ? 'ctx-value-muted' : undefined}>{fullnessLabel}</span>;
    }
    if (hasMeta && sessionMeta.numTurns != null && sessionMeta.numTurns > 0) {
      return <span key="turns">{sessionMeta.numTurns} msgs</span>;
    }
    return null;
  };

  // Hide the entire pill when there's nothing to show beyond the empty
  // "1st" placeholder — provider/model/tools are the only other inhabitants
  // of the pill, so if those are absent and we have no tokens/turns, the
  // pill is just visual noise. Loading state always renders (spinner).
  const hasAnyContent =
    loading || noSession || showsOccupancy ||
    (hasMeta && sessionMeta.numTurns != null && sessionMeta.numTurns > 0) ||
    hasProvider || !!modelLabel || toolCallCount > 0;
  if (!hasAnyContent) return null;

  // "Last updated" footer for expanded panel
  const lastHealthAt = health?.lastHealthAt;
  const renderLastUpdated = () => {
    if (!currentSessionId) return null;
    if (!lastHealthAt) return <div class="ctx-footer">Awaiting first response…</div>;
    const ago = formatAge(lastHealthAt);
    return <div class="ctx-footer">Updated {ago === 'now' ? 'just now' : `${ago} ago`}</div>;
  };

  return (
    <div class={`ctx-wrapper${isNavSlot ? ' ctx-wrapper--navslot' : ''}`} ref={rootRef}>
      {/* Compact pill \u2014 hidden in navslot when the session dot already shows the %.
          The outer wrapper + event listener stay mounted so the expanded sheet
          is still reachable via the 'va-open-contextbar' event from the dot tap. */}
      {!(hidePct && isNavSlot) && <button
        class={`ctx-pill${isNavSlot ? ' ctx-pill--navslot' : ''}${warn ? ' ctx-warn' : ''}${!tappable ? ' ctx-no-data' : ''}${isGoogle ? ' ctx-gemini' : ''}${loading ? ' ctx-loading' : ''}${!reading && tappable && !loading ? ' ctx-partial' : ''}`}
        style={{ color: loading ? undefined : color }}
        onClick={() => { if (tappable) setExpanded(!expanded); }}
        title={loading ? 'Fetching context\u2026' : !tappable ? 'No session' : showsOccupancy ? fullnessTitle : 'Context info'}
      >
        {isNavSlot ? (
          /* Bottom-nav r6: compact icon-over-label body \u2014 percentage over the
             context-window size, sized to sit in a bottom-nav slot. */
          <span class="ctx-navslot-body">
            {/* r18: percentage only \u2014 suppressed when the active session dot already shows it. */}
            <span class="ctx-navslot-pct">{loading ? <span class="ctx-loading-dot" /> : (showsOccupancy && !hidePct) ? fullnessLabel : '\u2014'}</span>
          </span>
        ) : (
          <>
            {hasProvider && <span class="ctx-provider-badge">{providerBadge.icon}{providerBadge.short}</span>}
            {renderPillContent()}
            {!loading && modelLabel && <span class="ctx-model-label">{modelLabel}</span>}
            {!loading && toolCallCount > 0 && <span class="ctx-tool-count" title={`${toolCallCount} tool calls`}> {toolCallCount}t</span>}
            {!loading && enable1mContext && <span class="ctx-1m-badge" title="1M context window active">1M</span>}
            {!loading && warn && ' \u26a0'}
          </>
        )}
      </button>}

      {/* Expanded overlay — rises as a fixed bottom-sheet above the nav in navslot mode */}
      {expanded && (
        <div class={`ctx-panel${isNavSlot ? ' ctx-panel--sheet' : ''}`}>
          {isNavSlot && <div class="drawer-grab" aria-hidden="true" onTouchStart={sheetSwipe.onTouchStart} onTouchEnd={sheetSwipe.onTouchEnd} />}
          {/* Provider — always first when available */}
          {hasProvider && (
            <div class="ctx-row">
              <span class="ctx-label">Provider</span>
              <span class="ctx-value">{providerBadge.icon} {providerBadge.name}</span>
            </div>
          )}
          {hasProvider && providerInfo!.model && (
            <div class="ctx-row">
              <span class="ctx-label">Model</span>
              <span class="ctx-value">{fullModelLabel(providerInfo!.model)}</span>
            </div>
          )}
          {hasProvider && providerInfo!.reason && (
            <div class="ctx-row">
              <span class="ctx-label">{providerInfo!.reason!.includes('Fallback') ? 'Notice' : 'Switched'}</span>
              <span class="ctx-value" style={{ color: '#cc8833' }}>{providerInfo!.reason}</span>
            </div>
          )}

          {/* Context usage — the server-computed union; placeholder only when pending */}
          <div class="ctx-row" style={hasProvider ? sectionStyle : undefined}>
            <span class="ctx-label">Context</span>
            <span class="ctx-value" style={isProjected ? { color: 'var(--text-secondary)' } : reading ? { color } : undefined} title={showsOccupancy ? fullnessTitle : undefined}>
              {showsOccupancy ? fullnessLabel : <span class="ctx-value-pending">pending</span>}
            </span>
          </div>
          {reading && (
            <div class="ctx-row">
              <span class="ctx-label">Tokens</span>
              <span class="ctx-value">{isProjected ? '~' : ''}{formatTokens(reading.contextTokens)} / {formatTokens(reading.contextWindow)}</span>
            </div>
          )}
          {fullness.kind === 'unknown-window' && (
            <div class="ctx-row">
              <span class="ctx-label">Tokens</span>
              <span class="ctx-value">{formatTokens(fullness.contextTokens)} / ?</span>
            </div>
          )}
          {warn && (
            <div class="ctx-alert">{'\u26a0'} Compaction approaching — older messages will be summarized</div>
          )}

          {/* Messages — show even if 0 (means "just started") */}
          <div class="ctx-row">
            <span class="ctx-label">Messages</span>
            <span class="ctx-value">{sessionMeta.numTurns ?? 0}</span>
          </div>

          {/* Tool calls */}
          <div class="ctx-row">
            <span class="ctx-label">Tool calls</span>
            <span class="ctx-value">{toolCallCount > 0 ? toolCallCount : <span class="ctx-value-pending">—</span>}</span>
          </div>

          {/* Cost */}
          <div class="ctx-row">
            <span class="ctx-label">Cost</span>
            <span class="ctx-value">
              {sessionMeta.totalCostUsd != null ? `$${sessionMeta.totalCostUsd.toFixed(3)}` : <span class="ctx-value-pending">—</span>}
            </span>
          </div>

          {/* Age */}
          <div class="ctx-row">
            <span class="ctx-label">Age</span>
            <span class="ctx-value">{health?.createdAt ? formatAge(health.createdAt) : <span class="ctx-value-pending">—</span>}</span>
          </div>

          {health?.fileSize != null && health.fileSize > 0 && (
            <div class="ctx-row">
              <span class="ctx-label">Session file</span>
              <span class="ctx-value">{(health.fileSize / 1024).toFixed(0)} KB</span>
            </div>
          )}
          {checkedKeys.length > 0 && (
            <div class="ctx-row">
              <span class="ctx-label">Projects</span>
              <span class="ctx-value">{checkedKeys.length}</span>
            </div>
          )}
          {/* Docs-update section */}
          {docsUpdateStats != null && (
            <div class="ctx-row" style={sectionStyle}>
              <span class="ctx-label">Docs saved</span>
              <span class="ctx-value" style={{ color: docsUpdateStats.count > 0 ? '#44cc88' : 'var(--text-dim)' }}>
                {docsUpdateStats.count > 0 ? `${docsUpdateStats.count}\u00d7` : 'none'}
                {docsUpdateStats.lastAt && <span class="ctx-value-muted"> \u00b7 {formatAge(docsUpdateStats.lastAt)} ago</span>}
              </span>
            </div>
          )}
           {/* Auto-save threshold — always visible when handler provided; stepper editable */}
          {onToggleAutoDocsPaused && (
            <div class="ctx-row ctx-toggle-row">
              <span class="ctx-toggle-label">Auto-save at</span>
              <div class="ctx-threshold-ctrl">
                <button
                  class="ctx-thr-btn"
                  onClick={(e) => { e.stopPropagation(); adjustThreshold(-THRESHOLD_STEP); }}
                  disabled={effectiveThreshold <= THRESHOLD_MIN}
                  title="Decrease threshold"
                >-</button>
                <span class="ctx-thr-val">{effectiveThreshold}%</span>
                <button
                  class="ctx-thr-btn"
                  onClick={(e) => { e.stopPropagation(); adjustThreshold(THRESHOLD_STEP); }}
                  disabled={effectiveThreshold >= THRESHOLD_MAX}
                  title="Increase threshold"
                >+</button>
              </div>
              <button
                id="ctx-autodocs-toggle"
                class={`ctx-toggle-switch${autoDocsPaused ? ' ctx-toggle-off' : ' ctx-toggle-on'}`}
                role="switch"
                aria-checked={!autoDocsPaused}
                title={autoDocsPaused ? 'Auto docs-update paused — click to resume' : 'Auto docs-update active — click to pause'}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAutoDocsPaused(!autoDocsPaused);
                }}
              >
                <span class="ctx-toggle-thumb" />
              </button>
            </div>
          )}
                   {/* Session tuning — effort ruler + 1M toggle (moved from InlineTuningControl) */}
          {(supportsEffort || supports1M) && (onEffortChange || on1mContextChange) && (
            <div class="ctx-tuning-section" style={sectionStyle}>
              {supportsEffort && onEffortChange && (
                <EffortRuler
                  value={effort ?? 'high'}
                  allowed={caps!.supportedEffortLevels!}
                  onChange={onEffortChange}
                  groupName="ctx-effort"
                  modelName={activeModel?.displayName ?? activeModel?.modelId}
                />
              )}
              {supports1M && on1mContextChange && (
                <div class="ctx-row ctx-toggle-row" style={supportsEffort ? { marginTop: '8px' } : undefined}>
                  <label class="ctx-toggle-label" for="ctx-1m-toggle">
                    1M context window
                  </label>
                  <button
                    id="ctx-1m-toggle"
                    class={`ctx-toggle-switch${enable1mContext ? ' ctx-toggle-on' : ' ctx-toggle-off'}`}
                    role="switch"
                    aria-checked={!!enable1mContext}
                    title={enable1mContext ? '1M context active — click to disable' : 'Enable 1M context window for this chat'}
                    onClick={(e) => {
                      e.stopPropagation();
                      on1mContextChange(!enable1mContext);
                    }}
                  >
                    <span class="ctx-toggle-thumb" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Models breakdown section */}
          {modelsUsed.length > 0 && (
            <div class="ctx-section" style={sectionStyle}>
              <div class="ctx-row">
                <span class="ctx-label">Models used</span>
                <span class="ctx-value">{distinctModels === 1 ? '1 model' : `${distinctModels} models`}</span>
              </div>
              {modelsUsed.map((m) => (
                <div key={m.model} class="ctx-row ctx-row-indent">
                  <span class="ctx-label">{fullModelLabel(m.model)}</span>
                  <span class="ctx-value ctx-value-muted">{'\u00d7'}{m.count}</span>
                </div>
              ))}
            </div>
          )}
          {/* Last updated footer */}
          {renderLastUpdated()}
        </div>
      )}
    </div>
  );
}
